import { Crown, Laptop } from "lucide-react";
import { ForeignMark } from "@/components/foreign-mark";
import { AVATAR_ICONS, avatarColorVar } from "@/lib/avatars";
import { linkPresence, presenceColor } from "@/lib/foreign-agents";
import { agentStatusColor, AGENT_STATUS_LABELS, type AgentWithContext } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * An agent's icon in its tint (`lib/avatars.tsx`), with its status as a dot. A foreign
 * agent also carries a link mark in the top corner, and while its machine is not
 * connected the dot says that rather than its status — nothing runs until it is back.
 */
export const AgentAvatar = ({
  agent,
  size = "md",
}: {
  agent: Pick<AgentWithContext, "name" | "status"> & Partial<Pick<AgentWithContext, "kind" | "icon" | "iconColor" | "link">>;
  size?: "sm" | "md" | "lg";
}) => {
  const foreign = agent.kind === "foreign";
  const presence = foreign ? linkPresence(agent.link) : null;
  // Before the backfill has run the master still gets its crown, anyone else initials.
  const Icon =
    (agent.icon ? AVATAR_ICONS[agent.icon] : undefined) ??
    (agent.kind === "master" ? Crown : foreign ? Laptop : undefined);
  const color = avatarColorVar(agent.iconColor) ?? (agent.kind === "master" ? "var(--avatar-violet)" : "var(--primary)");
  const away = presence !== null && presence !== "online";
  return (
    <span
      className={cn(
        "relative inline-grid shrink-0 place-items-center rounded-full font-semibold ring-1 ring-inset",
        size === "lg" ? "size-10 text-sm" : size === "sm" ? "size-6 text-[10px]" : "size-9 text-[13px]",
      )}
      style={{
        color,
        backgroundColor: `color-mix(in oklab, ${color} 16%, transparent)`,
        // @ts-expect-error CSS custom property for the ring color
        "--tw-ring-color": `color-mix(in oklab, ${color} 30%, transparent)`,
      }}
    >
      {Icon ? (
        <Icon aria-hidden strokeWidth={2} className={size === "lg" ? "size-5" : size === "sm" ? "size-3.5" : "size-[18px]"} />
      ) : (
        agent.name
          .split(/\s+/)
          .map((part) => part[0])
          .slice(0, 2)
          .join("")
          .toUpperCase()
      )}
      {presence ? <ForeignMark presence={presence} size={size === "sm" ? "sm" : "md"} /> : null}
      <span
        title={away ? (presence === "unpaired" ? "Not paired yet" : "Offline") : AGENT_STATUS_LABELS[agent.status]}
        className={cn(
          "absolute -bottom-0.5 -right-0.5 rounded-full border-2 border-background",
          size === "sm" ? "size-2.5" : "size-3",
          agent.status === "working" && !away && "animate-pulse",
        )}
        style={{ backgroundColor: away ? presenceColor(presence) : agentStatusColor(agent.status) }}
      />
    </span>
  );
};
